import { environment } from './../../../environments/environment';
import {
    HttpClient,
    HttpEventType,
    HttpHeaderResponse,
    HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { Observable, Subject } from 'rxjs';
import { GuiMsjService } from './gui-msj.service';
import { Solicitud } from '@interfaces/interface-bakend/ofertas-laborales/solicitudes.interface';
import { CryptoService } from '@services/crypto-js/crypto.service';

const URL_BACKEND = environment.urlBackend;

/**
 * Tamaño máximo permitido por archivo, en MB. Fuente única del límite de subida.
 */
export const MAX_ARCHIVO_MB = 50;

interface UploadResponse {
    ok: boolean;
    solicitud: Solicitud;
}

@Injectable({
    providedIn: 'root',
})
export class MediaService {
    private avance = new Subject<any>();
    contadorControl = 0;

    constructor(
        private authservice: AuthService,
        private httpClient: HttpClient,
        private guiMsjService: GuiMsjService,
        private cryptoService: CryptoService
    ) {}

    /* pre inscripción UPLOAD*/
    // SUBE A TMP LUEGO CUANDO TERMINO LA CARGA DE DOCUMENTO LOS PASA A POST
    uploadAttachJobOfferFromWeb(
        selectedFile: any,
        idUsers: string,
        idAdjunto: string,
        idSolicitud?: string
    ) {

        /* Cuando es un documento extra, no tiene _id de adjunto y lo seteo el id de la solicitud*/
        if (idAdjunto === 'idAdjunto') {
            idAdjunto = idSolicitud;
        }

        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idUsers,
                    idAdjunto,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(
                        `${URL_BACKEND}/media/uploadAttachJobOfferFromWeb`,
                        fd,
                        {
                            reportProgress: true,
                            observe: 'events',
                            headers,
                        }
                    )
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    console.log('4-SUBIDO');

                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    uploadOneAttachJobOfferFromWebEncrypted(
        selectedFile: any,
        idUsers: string,
        idAdjunto: string,
        idSolicitud: string,
        autorizo: boolean,
    ): Promise<UploadResponse> {
        if (idAdjunto === 'idAdjunto') {
            idAdjunto = idSolicitud;
        }

        return new Promise(async (resolve, reject) => {
            const fd = new FormData();
            
            // Headers sin token
            const headers = new HttpHeaders({
                idUsers,
                idSolicitud,
                idAdjunto,
                autorizo: autorizo.toString()
            });

            fd.append('file', selectedFile, selectedFile.name);

            this.httpClient
                .post<{ payload?: string; response?: { payload?: string } }>(
                    `${URL_BACKEND}/media/uploadOneAttachJobOfferFromWebEncrypted`,
                    fd,
                    {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    }
                )
                .subscribe({
                    next: (event: any) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            this.sendAvance(Math.round((event.loaded / event.total) * 100));
                        } else if (event.type === HttpEventType.Response) {
                            try {
                                console.log('🔍 Respuesta cruda upload:', event.body);

                                // Extraer payload encriptado de la respuesta
                                const payload = event.body?.payload ?? event.body?.response?.payload;

                                if (typeof payload !== 'string' || !payload) {
                                    throw new Error('El backend no retornó payload encriptado');
                                }

                                // Solo desencriptar la respuesta
                                const decrypted = this.cryptoService.decryptData(payload);
                                console.log('✅ Upload desencriptado OK');

                                if (decrypted.ok) {
                                    resolve(decrypted);
                                } else {
                                    reject(new Error('Upload falló'));
                                }
                            } catch (error) {
                                console.error('❌ Error al desencriptar upload:', error);
                                reject(error);
                            }
                        }
                    },
                    error: (err) => {
                        console.error('❌ Error HTTP upload:', err);
                        reject(err);
                    }
                });
        });
    }

    uploadOneAttachJobOfferFromWeb(
        selectedFile: any,
        idUsers: string,
        idAdjunto: string,
        idSolicitud: string,
        autorizo: boolean,
    ): Promise<UploadResponse> {
        /* Cuando es un documento extra, no tiene _id de adjunto y lo seteo con el id de la solicitud*/
        if (idAdjunto === 'idAdjunto') {
            idAdjunto = idSolicitud;
        }

        /*
         * Camino normal: multipart/form-data.
         * Si el nginx del hosting lo rechaza con 403 (bloquea multipart en todo
         * el vhost, antes de llegar al backend), reintentamos el MISMO archivo
         * como base64 dentro de un JSON, que sí pasa el filtro.
         */
        return this.enviarMultipart(
            selectedFile,
            idUsers,
            idAdjunto,
            idSolicitud,
            autorizo
        ).catch((error: any) => {
            const rawStatus = error?.status ?? error?.statusCode ?? error?.error?.statusCode;
            const status = rawStatus !== undefined && rawStatus !== null ? Number(rawStatus) : null;
            console.warn('⚠️ Falló multipart/form-data. Status recibido:', status, error);

            // Nginx 403 puede llegar como status 403 o status 0 (cross-origin / falta de CORS en la página de error de Nginx)
            const esBloqueoHosting = status === 403 || status === 0;
            if (!esBloqueoHosting) {
                throw error;
            }
            console.log('🔄 Reintentando subida por Base64 JSON (POST /media/uploadOneAttachJobOfferFromWebB64)...');
            return this.enviarBase64(
                selectedFile,
                idUsers,
                idAdjunto,
                idSolicitud,
                autorizo
            );
        });
    }

    /* Camino multipart: el de siempre */
    private enviarMultipart(
        selectedFile: any,
        idUsers: string,
        idAdjunto: string,
        idSolicitud: string,
        autorizo: boolean,
    ): Promise<UploadResponse> {
        return new Promise(async (resolve, reject) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idUsers,
                    idSolicitud,
                    idAdjunto,
                    autorizo: autorizo.toString() // Convertir a string
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(
                        `${URL_BACKEND}/media/uploadOneAttachJobOfferFromWeb`,
                        fd,
                        {
                            reportProgress: true,
                            observe: 'events',
                            headers,
                        }
                    )
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(event.body);
                                    } else {
                                        resolve({
                                            ok: false,
                                        } as UploadResponse);
                                    }
                                }
                            }
                        },
                        (error: any) => {
                            reject(error);
                            // El fallback reintenta por base64 los errores 403 o 0 del hosting.
                            // El 409 lo informa el llamador: no mostramos el aviso genérico.
                            const rawStatus =
                                error?.status ?? error?.statusCode ?? error?.error?.statusCode;
                            const status = rawStatus !== undefined && rawStatus !== null ? Number(rawStatus) : null;
                            const esBloqueoHosting = status === 403 || status === 0;
                            if (!esBloqueoHosting && status !== 409) {
                                this.guiMsjService.msjFormSubmit(
                                    'UploadError'
                                );
                            }
                        }
                    );
            });
        });
    }

    /* Camino JSON + base64: forzado por el bloqueo de multipart del hosting */
    private enviarBase64(
        selectedFile: any,
        idUsers: string,
        idAdjunto: string,
        idSolicitud: string,
        autorizo: boolean,
    ): Promise<UploadResponse> {
        return new Promise(async (resolve, reject) => {
            try {
                const token: any = await this.authservice.getJwToken();
                const dataUrl = await this.archivoABase64(selectedFile);
                const headers = new HttpHeaders({
                    'x-token': token,
                    idUsers,
                    idSolicitud,
                    idAdjunto,
                    autorizo: autorizo.toString(),
                });

                this.httpClient
                    .post(
                        `${URL_BACKEND}/media/uploadOneAttachJobOfferFromWebB64`,
                        {
                            // Sin el prefijo "data:...;base64,"
                            file: dataUrl.includes(',')
                                ? dataUrl.split(',')[1]
                                : dataUrl,
                            nombre: selectedFile.name,
                        },
                        { headers }
                    )
                    .subscribe({
                        next: (body: any) => {
                            this.sendAvance(100);
                            if (body && body.ok) {
                                resolve(body as UploadResponse);
                            } else {
                                resolve({ ok: false } as UploadResponse);
                            }
                        },
                        error: (error: any) => {
                            console.error(
                                '❌ Error HTTP upload (base64):',
                                error
                            );
                            reject(error);
                            // 409 = candado de estado: el llamador ya avisa.
                            const status =
                                error?.status ?? error?.statusCode;
                            if (status !== 409) {
                                this.guiMsjService.msjFormSubmit(
                                    'UploadError'
                                );
                            }
                        },
                    });
            } catch (error) {
                reject(error);
                this.guiMsjService.msjFormSubmit('UploadError');
            }
        });
    }

    private archivoABase64(file: any): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    /* CURSOS UPLOAD*/
    uploadImgCursoPresentacion(selectedFile: any, idcurso: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idcurso,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadImgCurso`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    /* COMPROBANTE UPLOAD*/
    uploadComprobanteTransferencia(
        selectedFile: any,
        idUsuario: string,
        idBankTransfer: string
    ) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idusuario: idUsuario,
                    idbanktransfer: idBankTransfer,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(
                        `${URL_BACKEND}/media/uploadComprobanteTransferencia`,
                        fd,
                        {
                            reportProgress: true,
                            observe: 'events',
                            headers,
                        }
                    )
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    uploadMp4CursoPresentacion(selectedFile: any, idcurso: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idcurso,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadMp4Curso`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe(
                        (event: any) => {
                            // console.log(event)
                            if (event.type === HttpEventType.UploadProgress) {
                                // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    /* ACTIVIDADES UPLOAD*/
    uploadImgActividad2(selectedFile: any, idactividad: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idactividad,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadImgActividad`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe((event: any) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                            this.sendAvance(
                                Math.round((event.loaded / event.total) * 100)
                            );
                        } else {
                            if (event.type === HttpEventType.Response) {
                                if (event.body['ok']) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            }
                        }
                    });
            });
        });
    }

    /* PLAN MEDICO PDF UPLOAD */
    uploadPdfPlan(selectedFile: any, idplan: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idplan,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadPdfPlan`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe((event: any) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            this.sendAvance(
                                Math.round((event.loaded / event.total) * 100)
                            );
                        } else {
                            if (event.type === HttpEventType.Response) {
                                if (event.body['ok']) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            }
                        }
                    });
            });
        });
    }

    /* IMG PERFIL UPLOAD*/
    uploadImgPerfil(selectedFile: any, idusers: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idusers,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadImgPerfil`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe((event: any) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                            this.sendAvance(
                                Math.round((event.loaded / event.total) * 100)
                            );
                        } else {
                            if (event.type === HttpEventType.Response) {
                                if (event.body['ok']) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            }
                        }
                    });
            });
        });
    }

    /* LECCIONES UPLOAD*/
    // tslint:disable-next-line: variable-name
    uploadLeccion(selectedFile: any, idleccion: string, type: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idleccion,
                    type,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadFileLeccion`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    // tslint:disable-next-line: variable-name
    uploadUpdateLeccion(selectedFile: any, idleccion: string, type: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idleccion,
                    type,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .put(`${URL_BACKEND}/media/uploadFileLeccionUpdate`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe((event: any) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                            this.sendAvance(
                                Math.round((event.loaded / event.total) * 100)
                            );
                        } else {
                            if (event.type === HttpEventType.Response) {
                                if (event.body['ok']) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            }
                        }
                    });
            });
        });
    }

    /* Controlar size file*/
    controlSize(f): boolean {
        const fsize = f.target.files[0].size;
        const file = Math.round(fsize / 1024);
        const maxSizeInKB = MAX_ARCHIVO_MB * 1024; // 50MB en KB
        let flag: boolean;

        /// The size of the file.
        if (file >= maxSizeInKB) {
            flag = false;
        } else {
            flag = true;
        }
        return flag;
    }

    /* Controlar size file of img - mp4 and pdf*/
    controlSize50mb(f): boolean {
        const fsize = f.target.files[0].size;
        const file = Math.round(fsize / 1024);
        let flag: boolean;

        /// The size of the file. 50mg
        // if (file >= 51200) {
        if (file >= 204800) {
            flag = false;
        } else {
            flag = true;
        }
        return flag;
    }

    /* OBSERBABLE UPLOAD*/
    clearAvance() {
        this.avance.next();
    }

    getAvance(): Observable<any> {
        return this.avance.asObservable();
    }

    sendAvance(avance: any) {
        this.avance.next({ value: avance });
    }

    getTypeFile(type): string {
        switch (type) {
            case 'image/jpeg': // img - image/png
                return 'img';
                break;

            case 'image/png': // img - image/png
                return 'img';
                break;

            case 'video/mp4': // mp4 - video/mp4
                return 'mp4';
                break;

            case 'application/pdf': // pdf - application/pdf
                return 'pdf';
                break;

            default:
                return 'sinEspecificar';
                break;
        }
    }

    uploadFileExamen(
        idExamen: string,
        pos: string,
        file: any,
        type: string,
        idP?: string,
        posRespuesta?: string
    ) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();

                const headers = new HttpHeaders({
                    'x-token': token,
                    idExamen,
                    pos,
                    type, // respuesta "R" o pregunta "P"
                    idPregunta: idP || 'SIN-ID',
                    posRespuesta: posRespuesta || 'SIN-POS-RESPUESTA',
                });

                fd.append('file', file, file.name);

                // this.httpClient.post(`${ URL_BACKEND}/media/uploadFileLeccion`, fd, {
                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadFileExamen`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                console.log(
                                    'Upload Progress => ' +
                                        Math.round(
                                            (event.loaded / event.total) * 100
                                        )
                                );
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(event.body);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    /* CURSOS UPLOAD*/
    uploadImgPortadaExamen(selectedFile: any, idexamen: string) {
        return new Promise(async (resolve) => {
            await this.authservice.getJwToken().then((token: any) => {
                const fd = new FormData();
                const headers = new HttpHeaders({
                    'x-token': token,
                    idexamen,
                });

                fd.append('file', selectedFile, selectedFile.name);

                this.httpClient
                    .post(`${URL_BACKEND}/media/uploadImgenExamen`, fd, {
                        reportProgress: true,
                        observe: 'events',
                        headers,
                    })
                    .subscribe(
                        (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                // console.log( 'Upload Progress => ' + Math.round(event.loaded / event.total * 100));
                                this.sendAvance(
                                    Math.round(
                                        (event.loaded / event.total) * 100
                                    )
                                );
                            } else {
                                if (event.type === HttpEventType.Response) {
                                    if (event.body['ok']) {
                                        resolve(true);
                                    } else {
                                        resolve(false);
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('error', error);
                            this.guiMsjService.msjFormSubmit('UploadError');
                        }
                    );
            });
        });
    }

    getTipoArchivo(tipo: string): string {
        // tslint:disable-next-line:prefer-const
        let tipoRachivo: string;
        switch (tipo) {
            case 'application/pdf':
                tipoRachivo = 'pdf';
                break;
            case 'video/mp4':
                tipoRachivo = 'mp4';
                break;
            case 'image/png':
                tipoRachivo = 'img';
                break;
            case 'image/jpeg':
                tipoRachivo = 'img';
                break;
            case 'image/jpg':
                tipoRachivo = 'img';
                break;
            default:
                break;
        }

        return tipoRachivo;
    }

    controlContador(): boolean {
        let flag = false;
        if (this.contadorControl > 1) {
            alert('Ya selecciono el documento, se esta procesando..');
            flag = false;
        } else {
            flag = true;
        }
        return flag;
    }

    incrementarContadorControlUpload() {
        this.contadorControl++;
    }

    reiniciarContadorControlUpload() {
        this.contadorControl = 0;
    }

    getContadorControlUpload() {
        return this.contadorControl;
    }
}
